import { assetURL } from '@features/app-hooks'
import type { TaxAppId } from '@st/tax-folder'
import {
  Button,
  DocumentDropZone,
  DropHere,
  DropProgress,
  ProgressStepIndicator,
  Wizard
} from '@st/theme'
import { useState, type ComponentType, type ReactNode } from 'react'
import { match, P } from '@st/pattern'
import { clientPacketsFlowDefinition } from '~/features/client-packets/client-packets-flow'
import type {
  FolderImportUploadStatus,
  CreateFolderImportSource,
  CreateFolderImportUploadFile
} from './create-folder-import-module'

type Props = {
  source: CreateFolderImportSource
  flow: CreateFolderImportUploadFile['flow']
  onClose: () => void
  onBack: () => void
  onDrop: (dataTransfer: DataTransfer) => void
  uploadState: FolderImportUploadStatus
}

type CreateFolderImportFlow = Props['flow']

const IMPORT_INSTRUCTIONS_BY_TAX_APP: Partial<
  Record<TaxAppId, ComponentType<ImportInstructionsProps>>
> = {
  axcess: AxcessImportInstructions,
  drake: DrakeImportInstructions,
  lacerte: LacerteImportInstructions,
  proseries: ProseriesImportInstructions,
  prosystem: ProsystemImportInstructions,
  ultratax: UltrataxImportInstructions,
  proconnect: ProconnectImportInstructions
}

export function UploadFolderImportWizard({
  source,
  flow,
  onClose,
  onBack,
  onDrop,
  uploadState
}: Props) {
  if (source.type == 'csv') {
    return (
      <CSVImportInstructions
        flow={flow}
        onClose={onClose}
        onBack={onBack}
        onDrop={onDrop}
        uploadState={uploadState}
      />
    )
  }

  const TaxAppImportInstructions = IMPORT_INSTRUCTIONS_BY_TAX_APP[source.taxApp.id]
  if (!TaxAppImportInstructions) {
    throw new Error(`Missing import instructions for ${source.taxApp.id}`)
  }

  return (
    <TaxAppImportInstructions
      taxYear={source.taxYear}
      flow={flow}
      onClose={onClose}
      onBack={onBack}
      onDrop={onDrop}
      uploadState={uploadState}
    />
  )
}

function VisualStep({
  title,
  subtitle,
  image,
  stepNumber,
  stepCount
}: {
  title?: string
  subtitle?: ReactNode
  image?: string
  stepNumber: number
  stepCount: number
}) {
  return (
    <Wizard.Main
      variant="wide"
      progress={
        stepCount > 1 && (
          <ProgressStepIndicator className="mt-4" stepNumber={stepNumber} stepCount={stepCount} />
        )
      }
      title={title}
      subtitle={subtitle}
    >
      <div className="flex h-[500px] w-[1000px] items-center justify-center">
        {image && <img className="h-[500px] object-contain" src={assetURL(image)} />}
      </div>
    </Wizard.Main>
  )
}

function UploadStep({
  title,
  subtitle,
  uploadTitle,
  uploadSubtitle,
  stepNumber,
  stepCount,
  onDrop,
  uploadState,
  children
}: {
  title?: string
  subtitle?: string
  uploadTitle: ReactNode
  uploadSubtitle: string
  stepNumber: number
  stepCount: number
  onDrop: (dataTransfer: DataTransfer) => void
  uploadState: FolderImportUploadStatus
  children?: ReactNode
}) {
  return (
    <Wizard.Main
      variant="wide"
      progress={
        stepCount > 1 && (
          <ProgressStepIndicator className="mt-4" stepNumber={stepNumber} stepCount={stepCount} />
        )
      }
      title={title}
      subtitle={subtitle}
    >
      {children}

      {uploadState.status == 'failed' && (
        <div className="mb-4 text-sm text-red-600">
          Upload failed. Please try again or go back to choose a different import source.
        </div>
      )}

      <DocumentDropZone className="w-[600px]" onDrop={onDrop}>
        {match(uploadState)
          .with({ status: 'idle' }, () => (
            <DropHere caption={uploadSubtitle}>{uploadTitle}</DropHere>
          ))
          .with({ status: 'failed' }, () => (
            <DropHere caption={uploadSubtitle}>{uploadTitle}</DropHere>
          ))
          .with({ status: 'preparing' }, () => (
            <DropProgress progress={0} caption="Preparing upload..." />
          ))
          .with({ status: 'uploading' }, (uploadState) => (
            <DropProgress
              progress={uploadState.progress.bytesTransferred / uploadState.progress.totalBytes}
              caption="Uploading..."
            />
          ))
          .run()}
      </DocumentDropZone>
    </Wizard.Main>
  )
}

function importInstructionsBreadcrumb(flow: CreateFolderImportFlow) {
  return match(flow)
    .with('normal', () => 'Generate binders')
    .with(P.union('personalized-checklists', 'personalized-pdf'), (flow) =>
      `Generate ${clientPacketsFlowDefinition(flow).plural}`
    )
    .exhaustive()
}

function importInstructionsTitle(flow: CreateFolderImportFlow, taxSoftware: string) {
  return `${importInstructionsBreadcrumb(flow)} from ${taxSoftware}`
}

function importInstructionsUploadSubtitle(flow: CreateFolderImportFlow) {
  return match(flow)
    .with('normal', () => '1040, 1120, 1120S, and 1065 supported')
    .with(P.union('personalized-checklists', 'personalized-pdf'), () => '')
    .exhaustive()
}

// Axcess
function AxcessImportInstructions({
  taxYear,
  onClose,
  onBack,
  onDrop,
  uploadState,
  flow
}: ImportInstructionsProps) {
  const [stepNumber, setStepNumber] = useState(1)
  const stepCount = 6

  function onNext() {
    setStepNumber(stepNumber + 1)
  }

  function onPrev() {
    if (stepNumber === 1) {
      onBack()
    } else {
      setStepNumber(stepNumber - 1)
    }
  }

  return (
    <Wizard.Scaffold
      breadcrumb={importInstructionsBreadcrumb(flow)}
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onPrev}>
            Back
          </Button>
          <Button size="l" variant="primary" disabled={stepNumber === stepCount} onClick={onNext}>
            Next
          </Button>
        </Wizard.BottomNav>
      }
    >
      {match(stepNumber)
        .with(1, () => (
          <VisualStep
            stepNumber={1}
            stepCount={6}
            title={importInstructionsTitle(flow, 'Axcess')}
            image="instructions/axcess/2024/axcess-instructions-1.png"
            subtitle={match(flow)
              .with(P.union('personalized-checklists', 'personalized-pdf'), (flow) => (
                <span>
                  Open up Axcess {taxYear}, <Highlight>the prior year tax software</Highlight> and
                  select the clients you would like to generate{' '}
                  {clientPacketsFlowDefinition(flow).plural} for.
                </span>
              ))
              .with('normal', () => (
                <span>
                  Open up Axcess {taxYear}, <Highlight>the prior year tax software</Highlight> and
                  select the clients you would like to add to StanfordTax.
                </span>
              ))
              .exhaustive()}
          />
        ))
        .with(2, () => (
          <VisualStep
            stepNumber={2}
            stepCount={6}
            title={importInstructionsTitle(flow, 'Axcess')}
            image="instructions/axcess/2024/axcess-instructions-2.png"
            subtitle={`Click “Utilities” and select “Transfer to Prosystem fx.” This does not require you to have ProSystem fx as you will not actually be transferring any data to ProSystem fx. (note: if the button is disabled, it is because you must have the “Return transfer” functional right permission assigned to use the Transfer to ProSystem fx feature.)`}
          />
        ))
        .with(3, () => (
          <VisualStep
            stepNumber={3}
            stepCount={6}
            title={importInstructionsTitle(flow, 'Axcess')}
            image="instructions/axcess/2024/axcess-instructions-3.png"
            subtitle={`Enter an “Office group name,” select an “Export file location” - and then click “Transfer” (note: we recommend creating a folder on your Desktop called StanfordTax and exporting to that folder)`}
          />
        ))
        .with(4, () => (
          <VisualStep
            stepNumber={4}
            stepCount={6}
            title={importInstructionsTitle(flow, 'Axcess')}
            image="instructions/axcess/2024/axcess-instructions-4.png"
            subtitle={`Wait for the process to complete (note: Axcess is preparing an export file at this step, and is not actually transferring returns to Prosystem fx)`}
          />
        ))
        .with(5, () => (
          <VisualStep
            stepNumber={5}
            stepCount={6}
            title={importInstructionsTitle(flow, 'Axcess')}
            image="instructions/axcess/2024/axcess-instructions-5.png"
            subtitle={`Once the process is complete you will see the name of the export file (all clients will be exported into the same .dat file). This is the file you'll be uploading to StanfordTax, so make note of the name.`}
          />
        ))
        .with(6, () => (
          <UploadStep
            stepNumber={6}
            stepCount={6}
            title={importInstructionsTitle(flow, 'Axcess')}
            subtitle={`Find the export file on your computer, and upload it below.`}
            uploadTitle={
              <>
                Drop your backup here <a className="text-blue-500">or browse</a>
              </>
            }
            uploadSubtitle={importInstructionsUploadSubtitle(flow)}
            uploadState={uploadState}
            onDrop={onDrop}
          />
        ))
        .run()}
    </Wizard.Scaffold>
  )
}

// Drake
function DrakeImportInstructions({
  taxYear,
  onClose,
  onBack,
  onDrop,
  uploadState,
  flow
}: ImportInstructionsProps) {
  const [stepNumber, setStepNumber] = useState(1)
  const stepCount = 5

  function onNext() {
    setStepNumber(stepNumber + 1)
  }

  function onPrev() {
    if (stepNumber === 1) {
      onBack()
    } else {
      setStepNumber(stepNumber - 1)
    }
  }

  return (
    <Wizard.Scaffold
      breadcrumb={importInstructionsBreadcrumb(flow)}
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onPrev}>
            Back
          </Button>
          <Button size="l" variant="primary" disabled={stepNumber === stepCount} onClick={onNext}>
            Next
          </Button>
        </Wizard.BottomNav>
      }
    >
      {match(stepNumber)
        .with(1, () => (
          <VisualStep
            stepNumber={1}
            stepCount={5}
            title={importInstructionsTitle(flow, 'Drake')}
            image="instructions/drake/drake-instructions-1.png"
            subtitle={
              <span>
                Open up Drake {taxYear}, <Highlight>the prior year tax software</Highlight>,{' '}
                {`and go to Tools > File Maintenance > Backup.`}
              </span>
            }
          />
        ))
        .with(2, () => (
          <VisualStep
            stepNumber={2}
            stepCount={5}
            title={importInstructionsTitle(flow, 'Drake')}
            image="instructions/drake/drake-instructions-2.png"
            subtitle={`Select where you'd like the backup to be stored on your computer, select "Selective Backup", and click "Backup."`}
          />
        ))
        .with(3, () => (
          <VisualStep
            stepNumber={3}
            stepCount={5}
            title={importInstructionsTitle(flow, 'Drake')}
            image="instructions/drake/drake-instructions-3.png"
            subtitle={`Select "Customize Backup" at the top and "Tax Returns" on the left side panel. Then select the clients you'd like to include in the backup, and click "Backup."`}
          />
        ))
        .with(4, () => (
          <VisualStep
            stepNumber={4}
            stepCount={5}
            title={importInstructionsTitle(flow, 'Drake')}
            image="instructions/drake/drake-instructions-4.png"
            subtitle={`After you click "Backup", Drake will generate your backup.`}
          />
        ))
        .with(5, () => (
          <UploadStep
            stepNumber={5}
            stepCount={5}
            title={importInstructionsTitle(flow, 'Drake')}
            subtitle={`Locate the backup file on your computer (the file name will look something like D22_DB_05-26-23_15-51.7z) and upload it below.`}
            uploadTitle={
              <>
                Drop your backup here <a className="text-blue-500">or browse</a>
              </>
            }
            uploadSubtitle={importInstructionsUploadSubtitle(flow)}
            uploadState={uploadState}
            onDrop={onDrop}
          />
        ))
        .run()}
    </Wizard.Scaffold>
  )
}

type ImportInstructionsProps = Omit<Props, 'source'> & { taxYear: number }

// Lacerte
function LacerteImportInstructions({
  taxYear,
  onClose,
  onBack,
  onDrop,
  uploadState,
  flow
}: ImportInstructionsProps) {
  const [stepNumber, setStepNumber] = useState(1)
  const stepCount = 5

  function onNext() {
    setStepNumber(stepNumber + 1)
  }

  function onPrev() {
    if (stepNumber === 1) {
      onBack()
    } else {
      setStepNumber(stepNumber - 1)
    }
  }

  return (
    <Wizard.Scaffold
      breadcrumb={importInstructionsBreadcrumb(flow)}
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onPrev}>
            Back
          </Button>
          <Button size="l" variant="primary" disabled={stepNumber === stepCount} onClick={onNext}>
            Next
          </Button>
        </Wizard.BottomNav>
      }
    >
      {match(stepNumber)
        .with(1, () => (
          <VisualStep
            stepNumber={1}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'Lacerte')}
            image="instructions/2025/create-folder-windows.jpeg"
            subtitle={`Create a new folder on your computer and call it StanfordTax. This is where you'll store the backups you export from Lacerte in the upcoming steps.`}
          />
        ))
        .with(2, () => (
          <VisualStep
            stepNumber={2}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'Lacerte')}
            image="instructions/2025/lacerte/02.png"
            subtitle={match(flow)
              .with(P.union('personalized-checklists', 'personalized-pdf'), (flow) => (
                <span>
                  Open up Lacerte {taxYear}, <Highlight>the prior year tax software</Highlight>, and
                  select the clients you would like to generate{' '}
                  {clientPacketsFlowDefinition(flow).plural} for. Then select 'Backup' within the
                  Client menu.
                </span>
              ))
              .with('normal', () => (
                <span>
                  Open up Lacerte {taxYear}, <Highlight>the prior year tax software</Highlight>, and
                  select the clients you would like to add to StanfordTax. Then select 'Backup'
                  within the Client menu.
                </span>
              ))
              .exhaustive()}
          />
        ))
        .with(3, () => (
          <VisualStep
            stepNumber={3}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'Lacerte')}
            image="instructions/2025/lacerte/03.png"
            subtitle={`Select the StanfordTax folder you created in Step 1 as the directory where you'd like the backup to be stored, and click "OK"`}
          />
        ))
        .with(4, () => (
          <VisualStep
            stepNumber={4}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'Lacerte')}
            image="instructions/2025/create-zip-windows.jpeg"
            subtitle={`Go to the "StanfordTax" folder you created in Step 1. The folder should now contain the backup contents. Right-click the folder and compress it to a ZIP file.`}
          />
        ))
        .with(5, () => (
          <UploadStep
            stepNumber={5}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'Lacerte')}
            subtitle={`Upload the .ZIP file below.`}
            uploadTitle={
              <>
                Drop your .zip here <a className="text-blue-500">or browse</a>
              </>
            }
            uploadSubtitle={importInstructionsUploadSubtitle(flow)}
            uploadState={uploadState}
            onDrop={onDrop}
          />
        ))
        .run()}
    </Wizard.Scaffold>
  )
}

// Proseries
function ProseriesImportInstructions({
  taxYear,
  onClose,
  onBack,
  onDrop,
  uploadState,
  flow
}: ImportInstructionsProps) {
  const [stepNumber, setStepNumber] = useState(1)
  const stepCount = 6

  function onNext() {
    setStepNumber(stepNumber + 1)
  }

  function onPrev() {
    if (stepNumber === 1) {
      onBack()
    } else {
      setStepNumber(stepNumber - 1)
    }
  }

  return (
    <Wizard.Scaffold
      breadcrumb={importInstructionsBreadcrumb(flow)}
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onPrev}>
            Back
          </Button>
          <Button size="l" variant="primary" disabled={stepNumber === stepCount} onClick={onNext}>
            Next
          </Button>
        </Wizard.BottomNav>
      }
    >
      {match(stepNumber)
        .with(1, () => (
          <VisualStep
            stepNumber={1}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSeries')}
            image="instructions/2025/create-folder-windows.jpeg"
            subtitle={`Create a new folder on your computer and call it StanfordTax. This is where you'll store the backups you export from ProSeries in the upcoming steps.`}
          />
        ))
        .with(2, () => (
          <VisualStep
            stepNumber={2}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSeries')}
            image="instructions/2025/proseries/02.png"
            subtitle={match(flow)
              .with(P.union('personalized-checklists', 'personalized-pdf'), (flow) => (
                <span>
                  Open up Proseries {taxYear}, <Highlight>the prior year tax software</Highlight>,
                  and go to "HomeBase." Select the clients you'd like to generate{' '}
                  {clientPacketsFlowDefinition(flow).plural} for. You can select multiple clients at
                  once by holding the shift key.
                </span>
              ))
              .with('normal', () => (
                <span>
                  Open up Proseries {taxYear}, <Highlight>the prior year tax software</Highlight>,
                  and go to "HomeBase." Select the clients you'd like to add to StanfordTax. You can
                  select multiple clients at once by holding the shift key.
                </span>
              ))
              .exhaustive()}
          />
        ))
        .with(3, () => (
          <VisualStep
            stepNumber={3}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSeries')}
            image="instructions/2025/proseries/03.png"
            subtitle={`Go to File > Client File Maintenance > Copy/Backup.`}
          />
        ))
        .with(4, () => (
          <VisualStep
            stepNumber={4}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSeries')}
            image="instructions/2025/proseries/04.png"
            subtitle={`Select the StanfordTax folder which you created in Step 1 as the directory where you'd like the backup to be stored on your computer, and click "Backup Clients."`}
          />
        ))
        .with(5, () => (
          <VisualStep
            stepNumber={5}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSeries')}
            image="instructions/2025/create-zip-windows.jpeg"
            subtitle={`Go to the "StanfordTax" folder you created in Step 1. The folder should now contain the backup contents. Right-click the folder and compress it to a ZIP file.`}
          />
        ))
        .with(6, () => (
          <UploadStep
            stepNumber={6}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSeries')}
            subtitle={`Upload the .ZIP file below.`}
            uploadTitle={
              <>
                Drop your .zip here <a className="text-blue-500">or browse</a>
              </>
            }
            uploadSubtitle={importInstructionsUploadSubtitle(flow)}
            uploadState={uploadState}
            onDrop={onDrop}
          />
        ))
        .run()}
    </Wizard.Scaffold>
  )
}

// Prosystem
function ProsystemImportInstructions({
  taxYear,
  onClose,
  onBack,
  onDrop,
  uploadState,
  flow
}: ImportInstructionsProps) {
  const [stepNumber, setStepNumber] = useState(1)
  const stepCount = 10

  function onNext() {
    setStepNumber(stepNumber + 1)
  }

  function onPrev() {
    if (stepNumber === 1) {
      onBack()
    } else {
      setStepNumber(stepNumber - 1)
    }
  }

  return (
    <Wizard.Scaffold
      breadcrumb={importInstructionsBreadcrumb(flow)}
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onPrev}>
            Back
          </Button>
          <Button size="l" variant="primary" disabled={stepNumber === stepCount} onClick={onNext}>
            Next
          </Button>
        </Wizard.BottomNav>
      }
    >
      {match(stepNumber)
        .with(1, () => (
          <VisualStep
            stepNumber={1}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/create-folder-windows.jpeg"
            subtitle={`Create a new folder on your computer and call it StanfordTax. This is where you'll store the backups you export from ProSystem fx in the upcoming steps.`}
          />
        ))
        .with(2, () => (
          <VisualStep
            stepNumber={2}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/prosystem/02.png"
            subtitle={`In Office Manager, select "Backup Client Data"`}
          />
        ))
        .with(3, () => (
          <VisualStep
            stepNumber={3}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/prosystem/03.png"
            subtitle={`Select "All Clients" and click "OK" (note: you'll be able to select specific clients in the next step).`}
          />
        ))
        .with(4, () => (
          <VisualStep
            stepNumber={4}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/prosystem/04.png"
            subtitle={match(flow)
              .with(
                P.union('personalized-checklists',
                'personalized-pdf'),
                (flow) =>
                  `Select the clients you'd like to generate ${
                    clientPacketsFlowDefinition(flow).plural
                  } for, and click "Add." You can also click "Add All" to add all clients.`
              )
              .with(
                'normal',
                () =>
                  `Select the clients you'd like to add to StanfordTax, and click "Add." You can also click "Add All" to add all clients.`
              )
              .exhaustive()}
          />
        ))
        .with(5, () => (
          <VisualStep
            stepNumber={5}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/prosystem/05.png"
            subtitle={`Click "Select Drive"`}
          />
        ))
        .with(6, () => (
          <VisualStep
            stepNumber={6}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/prosystem/06.png"
            subtitle={`Select the "StanfordTax" folder which you created in Step 1, and then click "OK" (note: you might have to double-click the directory name "StanfordTax" before clicking "OK")`}
          />
        ))
        .with(7, () => (
          <VisualStep
            stepNumber={7}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/prosystem/07.png"
            subtitle={`If you have previous backups stored on your computer for the same clients, you'll see a popup like the one below. Select "Overwrite All Files."`}
          />
        ))
        .with(8, () => (
          <VisualStep
            stepNumber={8}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/prosystem/08.png"
            subtitle={`In addition to the backup file, Prosystem generates a PDF report of the backup. Choose any name you'd like for the report, and click "Save."`}
          />
        ))
        .with(9, () => (
          <VisualStep
            stepNumber={9}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            image="instructions/2025/create-zip-windows.jpeg"
            subtitle={`Go to the StanfordTax folder you created in the first step. The folder should now contain the backup contents. Right-click the folder and compress it to a ZIP file.`}
          />
        ))
        .with(10, () => (
          <UploadStep
            stepNumber={10}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'ProSystem fx')}
            subtitle={`Upload the .ZIP file below.`}
            uploadTitle={
              <>
                Drop your .zip here <a className="text-blue-500">or browse</a>
              </>
            }
            uploadSubtitle={importInstructionsUploadSubtitle(flow)}
            uploadState={uploadState}
            onDrop={onDrop}
          />
        ))
        .run()}
    </Wizard.Scaffold>
  )
}

// Ultratax
function UltrataxImportInstructions({
  taxYear,
  onClose,
  onBack,
  onDrop,
  uploadState,
  flow
}: ImportInstructionsProps) {
  const [stepNumber, setStepNumber] = useState(1)
  const stepCount = 9

  function onNext() {
    setStepNumber(stepNumber + 1)
  }

  function onPrev() {
    if (stepNumber === 1) {
      onBack()
    } else {
      setStepNumber(stepNumber - 1)
    }
  }

  return (
    <Wizard.Scaffold
      breadcrumb={importInstructionsBreadcrumb(flow)}
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onPrev}>
            Back
          </Button>
          <Button size="l" variant="primary" disabled={stepNumber === stepCount} onClick={onNext}>
            Next
          </Button>
        </Wizard.BottomNav>
      }
    >
      {match(stepNumber)
        .with(1, () => (
          <VisualStep
            stepNumber={1}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'UltraTax')}
            image="instructions/2025/create-folder-windows.jpeg"
            subtitle={`Create a new folder on your computer and call it StanfordTax. This is where you'll store the backups you export from UltraTax in the upcoming steps.`}
          />
        ))
        .with(2, () => (
          <VisualStep
            stepNumber={2}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'UltraTax')}
            image="instructions/2025/ultratax/02.png"
            subtitle={
              <span>
                Open up UltraTax {taxYear}, <Highlight>the prior year tax software</Highlight>,{' '}
                {` and select "Backup" within the File menu.`}
              </span>
            }
          />
        ))
        .with(3, () => (
          <VisualStep
            stepNumber={3}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'UltraTax')}
            image="instructions/2025/ultratax/03.png"
            subtitle={match(flow)
              .with(
                P.union('personalized-checklists',
                'personalized-pdf'),
                (flow) =>
                  `Select the clients you'd like to generate ${
                    clientPacketsFlowDefinition(flow).plural
                  } for, and click "Select." You can also click "Select All" if you'd like to add all clients.`
              )
              .with(
                'normal',
                () =>
                  `Select the clients you'd like to add to StanfordTax, and click "Select." You can also click "Select All" if you'd like to add all clients.`
              )
              .exhaustive()}
          />
        ))
        .with(4, () => (
          <VisualStep
            stepNumber={4}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'UltraTax')}
            image="instructions/2025/ultratax/04.png"
            subtitle={`Select the StanfordTax folder which you created in Step 1 as the directory where you'd like the backup to be stored.`}
          />
        ))
        .with(5, () => (
          <VisualStep
            stepNumber={5}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'UltraTax')}
            image="instructions/2025/ultratax/05.png"
            subtitle={`Uncheck the "Encrypt client backup" checkbox.`}
          />
        ))
        .with(6, () => (
          <VisualStep
            stepNumber={6}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'UltraTax')}
            image="instructions/2025/ultratax/06.png"
            subtitle={`Click "Backup"`}
          />
        ))
        .with(7, () => (
          <VisualStep
            stepNumber={7}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'UltraTax')}
            image="instructions/2025/ultratax/07.png"
            subtitle={`Click "Done"`}
          />
        ))
        .with(8, () => (
          <VisualStep
            stepNumber={8}
            stepCount={stepCount}
            title={importInstructionsTitle(flow, 'UltraTax')}
            image="instructions/2025/create-zip-windows.jpeg"
            subtitle={`Go to the StanfordTax folder you created in the first step. The folder should now contain the backup contents. Right-click the folder and compress it to a ZIP file.`}
          />
        ))
        .with(9, () => (
          <UploadStep
            stepNumber={9}
            stepCount={9}
            title={importInstructionsTitle(flow, 'UltraTax')}
            subtitle={`Upload the .ZIP file below.`}
            uploadTitle={
              <>
                Drop your .zip here <a className="text-blue-500">or browse</a>
              </>
            }
            uploadSubtitle={importInstructionsUploadSubtitle(flow)}
            uploadState={uploadState}
            onDrop={onDrop}
          />
        ))
        .run()}
    </Wizard.Scaffold>
  )
}

// Proconnect
function ProconnectImportInstructions({
  onClose,
  onBack,
  onDrop,
  uploadState,
  flow
}: ImportInstructionsProps) {
  const [stepNumber, setStepNumber] = useState(1)
  const stepCount = 2

  function onNext() {
    setStepNumber(stepNumber + 1)
  }

  function onPrev() {
    if (stepNumber === 1) {
      onBack()
    } else {
      setStepNumber(stepNumber - 1)
    }
  }

  return (
    <Wizard.Scaffold
      breadcrumb={importInstructionsBreadcrumb(flow)}
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onPrev}>
            Back
          </Button>
          <Button size="l" variant="primary" disabled={stepNumber === stepCount} onClick={onNext}>
            Next
          </Button>
        </Wizard.BottomNav>
      }
    >
      {match(stepNumber)
        .with(1, () => (
          <VisualStep
            stepNumber={1}
            stepCount={2}
            title={importInstructionsTitle(flow, 'ProConnect')}
            image="instructions/proconnect/pro-connect-call-icon.png"
            subtitle={`Our integration with ProConnect uses what's called a “Lacerte Data File.” To get this file, contact ProConnect support by requesting a phone callback through their help portal (https://ito.intuit.com/app/protax/conversation) or by calling them directly at 1-800-200-7599. They will prepare the Lacerte file for you and send you a link to download the file. Note: they will tell you that it takes 4-6 weeks to prepare the file, but most StanfordTax users receive it within one week.`}
          />
        ))
        .with(2, () => (
          <UploadStep
            stepNumber={2}
            stepCount={2}
            title={importInstructionsTitle(flow, 'ProConnect')}
            subtitle={`Upload the Lacerte Data File below`}
            uploadTitle={
              <>
                Drop your file here <a className="text-blue-500">or browse</a>
              </>
            }
            uploadSubtitle={importInstructionsUploadSubtitle(flow)}
            uploadState={uploadState}
            onDrop={onDrop}
          />
        ))
        .run()}
    </Wizard.Scaffold>
  )
}

function CSVImportInstructions({
  onClose,
  onBack,
  onDrop,
  uploadState,
  flow
}: Omit<Props, 'source'>) {
  function onPrev() {
    onBack()
  }

  return (
    <Wizard.Scaffold
      breadcrumb={importInstructionsBreadcrumb(flow)}
      onClose={onClose}
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onPrev}>
            Back
          </Button>
        </Wizard.BottomNav>
      }
    >
      <UploadStep
        stepNumber={1}
        stepCount={1}
        title={importInstructionsTitle(flow, 'CSV')}
        subtitle={`Download the sample CSV templates and fill them out with your clients' information. Then upload them below.`}
        uploadTitle={
          <>
            Drop your .csv files here <a className="text-blue-500">or browse</a>
          </>
        }
        uploadSubtitle={importInstructionsUploadSubtitle(flow)}
        uploadState={uploadState}
        onDrop={onDrop}
      >
        <div className="mb-4 flex flex-row gap-2">
          <Button variant="default" href="/samples/stanfordtax-sample-individual.csv">
            Download Individual CSV Template
          </Button>
          <Button variant="default" href="/samples/stanfordtax-sample-corporate.csv">
            Download Corporate CSV Template
          </Button>
        </div>
      </UploadStep>
    </Wizard.Scaffold>
  )
}

function Highlight({ children }: { children: ReactNode }) {
  return <span className="bg-yellow-200 py-0.5">{children}</span>
}
